import React from 'react';
import { useTranslation } from 'react-i18next';

interface DataTableProps {
  tableData: string[][];
}

const DataTable: React.FC<DataTableProps> = ({ tableData }) => {
  const { t } = useTranslation('common');
  
  if (!tableData || tableData.length === 0) {
    return <div className="empty-data-message">{t('common.noDataAvailable')}</div>;
  }

  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            {tableData[0].map((header, index) => (
              <th key={index} scope="col" className="data-table-header">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.slice(1, 6).map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'data-table-row-even' : 'data-table-row-odd'}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="data-table-cell">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Custom comparison function for better performance
const areEqual = (prevProps: DataTableProps, nextProps: DataTableProps) => {
  // Fast check if references are the same
  if (prevProps.tableData === nextProps.tableData) {
    return true;
  }
  
  // Check if lengths are different
  if (prevProps.tableData.length !== nextProps.tableData.length) {
    return false;
  }
  
  // Deep comparison only for visible rows (first 6)
  const visibleRows = Math.min(6, prevProps.tableData.length);
  for (let i = 0; i < visibleRows; i++) {
    const prevRow = prevProps.tableData[i];
    const nextRow = nextProps.tableData[i];
    
    if (prevRow.length !== nextRow.length) {
      return false;
    }
    
    for (let j = 0; j < prevRow.length; j++) {
      if (prevRow[j] !== nextRow[j]) {
        return false;
      }
    }
  }
  
  return true;
};

export default React.memo(DataTable, areEqual);